import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams, type NavigateOptions, type To } from "react-router-dom";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import {
  resolvePostSubmitRedirect,
  useFormResponseDialogShallowStore,
  type EnduserFormWizardProps,
} from "@tellescope/forms";
import type { Form, FormResponse } from "@tellescope/types-models";
import { BASE_PATH } from "../../../../config/env";
import { buildFormAccessCodeRoute } from "../../../../config/portal";
import { compareGroupPosition } from "../../../hooks/useFormGroupEntryRedirect";

interface UseFormGroupContinuationArgs {
  formResponse: Pick<FormResponse, "id" | "groupId" | "groupInstance">;
  form: Pick<Form, "customization">;
}

/**
 * Where an enduser goes after finishing a form opened by access code, in the legacy portal's order:
 * `?onboarding=true` → `?redirectTo=` → the next unsubmitted form in the same group send → the thanks
 * screen when the form opts in, otherwise the Documents page. Form-level redirects
 * (`submitRedirectURL`, a booked appointment, `back`, the server's `redirectTo`) are resolved by the
 * wizard first, as legacy's later `location.href` assignments overrode this choice.
 */
export const useFormGroupContinuation = ({ formResponse, form }: UseFormGroupContinuationArgs) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const { id, groupId, groupInstance } = formResponse;
  const isOnboarding = searchParams.get("onboarding") === "true";
  const redirectToParam = searchParams.get("redirectTo");
  const incomingBackUrl = searchParams.get("back") ?? undefined;
  const showThanks = !!form.customization?.portalShowThanksAfterSubmission;
  const { reset: resetFormStore } = useFormResponseDialogShallowStore("reset");

  // The wizard's store lives at module scope and outlives this page, so every hand-over clears it:
  // otherwise the next form opens on this one's field stack, at its last page with nothing rendered.
  const leaveTo = useCallback(
    (to: To, options?: NavigateOptions) => {
      resetFormStore();
      navigate(to, options);
    },
    [resetFormStore, navigate]
  );

  // Legacy chained only within a single send, so a response missing either half never chains.
  const { data: unsubmittedInGroup } = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: { filter: { groupId, groupInstance, submittedAt: { _exists: false } } },
    select: (data) => data.pages.flat(),
    enabled: !!groupId && !!groupInstance,
    // A legacy-iframe submit bypasses this cache, so each form re-reads its siblings rather than
    // trusting a list that may still show the form before it as open.
    staleTime: 0,
    gcTime: 0,
  });

  const nextAccessCode = (unsubmittedInGroup ?? [])
    .filter((r) => r.id !== id && !!r.accessCode)
    .sort(compareGroupPosition)[0]?.accessCode;

  // Normalized like `back`, the other destination taken from the query string rather than from org
  // configuration: a scheme-less value stays on the portal and a `javascript:` target is declined.
  const redirectToTarget = redirectToParam
    ? resolvePostSubmitRedirect({
        submitRedirectURL: undefined,
        backUrl: redirectToParam,
        serverRedirectUrl: undefined,
      })
    : undefined;

  const continueToNextStep = useCallback((): boolean => {
    if (isOnboarding) {
      leaveTo("/documents?onboarding=true");
      return true;
    }
    if (redirectToTarget) {
      window.location.href = redirectToTarget;
      return true;
    }
    if (nextAccessCode) {
      leaveTo({ pathname: buildFormAccessCodeRoute(nextAccessCode), search });
      return true;
    }
    return false;
  }, [isOnboarding, redirectToTarget, nextAccessCode, leaveTo, search]);

  const continueAfterSubmit = useCallback((): boolean => {
    if (continueToNextStep()) return true;
    if (showThanks) return false;
    leaveTo("/documents");
    return true;
  }, [continueToNextStep, showThanks, leaveTo]);

  const finish = useCallback(() => {
    if (!continueToNextStep()) leaveTo("/documents");
  }, [continueToNextStep, leaveTo]);

  // A Redirect field leaves the group for another form; `back` brings the enduser to the group's
  // entry link once that form is submitted, which then opens whatever is still unsubmitted.
  const groupBackUrl =
    groupId && groupInstance
      ? `${window.location.origin}${BASE_PATH}/documents?${new URLSearchParams({ groupId, groupInstance })}`
      : undefined;
  const redirectFieldBackUrl = groupBackUrl ?? incomingBackUrl;

  const continueToFormInApp = useCallback<NonNullable<EnduserFormWizardProps["continueToFormInApp"]>>(
    ({ accessCode }) => {
      if (!accessCode) return false;
      leaveTo(
        {
          pathname: buildFormAccessCodeRoute(accessCode),
          search: redirectFieldBackUrl ? `?${new URLSearchParams({ back: redirectFieldBackUrl })}` : "",
        },
        { replace: true }
      );
      return true;
    },
    [leaveTo, redirectFieldBackUrl]
  );

  return {
    /** Wizard hook: `true` when it navigated away, `false` to show the thanks screen. */
    continueAfterSubmit,
    /** The thanks screen's Done, and the autosubmit path that reaches it without a submit click. */
    finish,
    continueToFormInApp,
    /** The `back` this form was opened with, which the wizard follows once it is submitted. */
    documentGroupBackUrl: incomingBackUrl,
    /** For links that leave the form mid-way, which route around the wizard's own exits. */
    resetFormStore,
  };
};
